import mapValues from 'lodash/mapValues';
import pickBy from 'lodash/pickBy';

import { isMagicallyPopulatedDataInputKey } from './workflowJsonSelectors/isMagicallyPopulatedDataInputKey';
import {
  DATA_HANDLE_IDENTIFIER,
  SOURCE_HANDLE_IDENTIFIER,
  TARGET_HANDLE_IDENTIFIER,
  TASK_DEPENDENCY_HANDLE_IDENTIFIER,
} from '../constants';
import { DataHandleComponentData } from '../types/taskNode';
import uuid, { shortUuid } from '../uuid';

export const toDataHandleId = (
  type: 'source' | 'target',
  taskId: string,
  handleName: string
) =>
  `${
    type === 'source' ? SOURCE_HANDLE_IDENTIFIER : TARGET_HANDLE_IDENTIFIER
  }.${taskId}_${handleName.replace('.', '')}_${DATA_HANDLE_IDENTIFIER}`;

export const toTaskDependencyHandleId = (
  type: typeof SOURCE_HANDLE_IDENTIFIER | typeof TARGET_HANDLE_IDENTIFIER,
  taskId: string
) => `${type}.${taskId}_${TASK_DEPENDENCY_HANDLE_IDENTIFIER}`;

export const getUniqueId = ({
  type,
  message,
}: {
  type: string;
  message: string;
}) => [type, message, uuid()].join('___');

/**
 * Filters the object with arrays of inputs/outputs for all tasks.
 *
 * @param dataHandlesByTaskID - object with arrays of inputs/outputs by taskId.
 * @param handle - type of filtration.
 *
 * @returns an object with nonempty arrays of inputs or outputs for all task.
 */
export const filterCodeTaskDataHandlesByTaskId = (
  dataHandlesByTaskID: Record<string, DataHandleComponentData[]>,
  handle: 'input' | 'output'
): Record<string, DataHandleComponentData[]> => {
  return pickBy(
    mapValues(dataHandlesByTaskID, (dataHandles) =>
      dataHandles.filter((dataHandle) => {
        if (handle === 'input') {
          return (
            dataHandle.handleType === 'DATA_INPUT' &&
            !isMagicallyPopulatedDataInputKey(dataHandle.dataKey)
          );
        } else {
          return dataHandle.handleType === 'DATA_OUTPUT';
        }
      })
    ),
    (array) => array.length > 0
  );
};

/**
 * Generates a meaningful ID for a time constraint based on the connected tasks
 * Format: fromTaskId-sourceType_to_toTaskId-targetType_shortUuid
 * @param sourceTaskId - ID of the source task
 * @param targetTaskId - ID of the target task
 * @returns A meaningful time constraint ID string
 */
export const generateTimeConstraintId = (
  sourceTaskId: string,
  targetTaskId: string
): string => {
  const short = shortUuid();
  return `${sourceTaskId}_to_${targetTaskId}_${short}`;
};
